import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { DeliveryDataForm } from '../../../../app/models/Delivery';
import { SalesOrder } from '../../../../app/models/SalesOrder';
import { Location } from '../../../../app/models/Location';
import { Society } from '../../../../app/models/Society';
import { Client } from '../../../../app/models/Client';
import useHandleErrorFields from '../../../../hooks/useHandleErrorFields';
import SelectTwo, { SelectTwoMapperOptions } from '../../../../components/SelectTwo';

interface Props {
    delivery: DeliveryDataForm;
    setDelivery: (delivery: DeliveryDataForm) => void;
    salesOrders: SalesOrder[];
    locations: Location[];
    societies: Society[];
    clients: Client[];
    errorFields?: any;
    is_edit?: boolean;
    is_locked?: boolean;
    onSalesOrderChange?: (salesOrderId: number | null) => void;
}

const formatDateDisplay = (dateStr: string) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const formatDateTimeDisplay = (isoDate: string) => {
    if (!isoDate) return '';
    const d = new Date(isoDate);
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

const DeliveryForm = ({ delivery, setDelivery, salesOrders, locations, societies, clients, errorFields, is_edit, is_locked = false, onSalesOrderChange }: Props) => {
    const { fieldErrorMessage, fieldHasError, onFocusRemove, setErrors } = useHandleErrorFields();

    useEffect(() => {
        setErrors(errorFields);
    }, [errorFields]);

    const handleChangeSelectTwo = (name: string, value: number | string | null) => {
        // Si cambia el pedido, heredamos también destino (si existe)
        if (name === 'sales_order_id') {
            const salesOrderId = (value as number | null) ?? null;
            const so = salesOrders.find(order => order.id === salesOrderId);

            setDelivery({
                ...delivery,
                sales_order_id: salesOrderId ?? undefined,
                destination_id: (so as any)?.destination_id ?? delivery.destination_id,
            });

            if (onSalesOrderChange) onSalesOrderChange(salesOrderId);
            return;
        }

        setDelivery({ ...delivery, [name]: value });
    };

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>
    ) => {
        const { name, value } = e.target;
        setDelivery({ ...delivery, [name]: value });
    };

    // Preparar opciones de pedidos
    const salesOrderOptions = salesOrders
        .filter((order) => order.id !== undefined)
        .map((order) => {
            return {
                value: order.id as number,
                label: `Pedido #${order.id}`
            };
        });

    // Encontrar el pedido seleccionado para mostrar sociedad y cliente
    const selectedSalesOrder = salesOrders.find(order => order.id === delivery.sales_order_id);
    const displaySociety = selectedSalesOrder?.society || '';
    const displayClient = selectedSalesOrder?.client || '';
    
    // Parsear el HTML para mostrar correctamente (remover <br/> y mostrar en líneas separadas)
    const parseSocietyDisplay = (societyStr: string) => {
        if (!societyStr) return '';
        return societyStr.replace(/<br\s*\/?>/gi, ' - ');
    };
    
    const parseClientDisplay = (clientStr: string) => {
        if (!clientStr) return '';
        return clientStr.replace(/<br\s*\/?>/gi, ' - ');
    };

    // En edición, si el destino aún no está seteado, heredarlo desde el pedido
    useEffect(() => {
        if (!is_edit) return;
        if (!delivery.sales_order_id) return;
        if (delivery.destination_id) return;
        const so = salesOrders.find(o => o.id === delivery.sales_order_id);
        const destId = (so as any)?.destination_id;
        if (destId) {
            setDelivery({ ...delivery, destination_id: destId });
        }
    }, [is_edit, delivery.sales_order_id, delivery.destination_id, salesOrders.length]);

    return (
        <>
            {/* Pedido */}
            <div className="mb-3">
                <label className="form-label mb-1">
                    Pedido *
                    {delivery.sales_order_id && (
                        <>
                            {' — '}
                            <Link
                                to={`/sales-orders/${delivery.sales_order_id}/edit`}
                                className="text-decoration-none"
                                style={{ fontSize: '0.9em' }}
                            >
                                Ver pedido #{delivery.sales_order_id}
                            </Link>
                        </>
                    )}
                </label>
                <SelectTwo
                    name="sales_order_id"
                    id="sales_order_id"
                    inputValue={delivery.sales_order_id}
                    options={salesOrderOptions}
                    hasError={fieldHasError('sales_order_id') !== ''}
                    onChange={(value: any) =>
                        handleChangeSelectTwo('sales_order_id', value?.value)
                    }
                    placeholder="Seleccione un pedido"
                    onFocus={() => onFocusRemove('sales_order_id')}
                    isClearable={false}
                    disabled={is_edit === true || is_locked}
                />
                <div className="invalid-feedback" style={{ display: 'flex' }}>
                    {fieldErrorMessage('sales_order_id')}
                </div>
            </div>

            {/* Sociedad (heredada del pedido - solo lectura) */}
            <div className="mb-3">
                <label className="form-label">Sociedad</label>
                <input
                    type="text"
                    className="form-control"
                    value={parseSocietyDisplay(displaySociety)}
                    disabled
                    readOnly
                    style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                />
                <small className="form-text text-muted">Heredada del pedido seleccionado</small>
            </div>

            {/* Cliente (heredado del pedido - solo lectura) */}
            <div className="mb-3">
                <label className="form-label">Cliente</label>
                <input
                    type="text"
                    className="form-control"
                    value={parseClientDisplay(displayClient)}
                    disabled
                    readOnly
                    style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                />
                <small className="form-text text-muted">Heredado del pedido seleccionado</small>
            </div>

            {/* Destino - Solo en edición (arriba de Fecha de Entrega) */}
            {is_edit && (
                <div className="mb-3">
                    <label className="form-label">Destino</label>
                    <SelectTwo
                        name="destination_id"
                        id="destination_id"
                        inputValue={delivery.destination_id}
                        options={SelectTwoMapperOptions(locations, 'id', ['name'])}
                        hasError={fieldHasError('destination_id') !== ''}
                        onChange={(value: any) => handleChangeSelectTwo('destination_id', value?.value)}
                        placeholder="Seleccione un destino"
                        onFocus={() => onFocusRemove('destination_id')}
                        isClearable={true}
                        disabled={is_locked}
                    />
                    <div className="invalid-feedback" style={{ display: 'flex' }}>
                        {fieldErrorMessage('destination_id')}
                    </div>
                </div>
            )}

            {/* Fecha de Entrega - Solo en edición */}
            {is_edit && (
                <>
                    <div className="mb-3">
                        <label className="form-label">Fecha de Entrega</label>
                        <input
                            type="date"
                            className={`form-control ${fieldHasError('delivery_date')}`}
                            name="delivery_date"
                            value={delivery.delivery_date || ''}
                            onChange={handleChange}
                            onFocus={() => onFocusRemove('delivery_date')}
                            disabled={is_locked}
                            readOnly={is_locked}
                        />
                        <div className="invalid-feedback" style={{ display: 'flex' }}>
                            {fieldErrorMessage('delivery_date')}
                        </div>
                    </div>

                    {delivery.actual_delivery_date && (
                        <div className="mb-3">
                            <label className="form-label">Fecha real de entrega</label>
                            <input
                                type="text"
                                className="form-control"
                                value={formatDateDisplay(delivery.actual_delivery_date)}
                                readOnly
                                disabled
                                style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                            />
                        </div>
                    )}

                    {delivery.actual_delivery_registered_at && (
                        <div className="mb-3">
                            <label className="form-label">Registrado el</label>
                            <input
                                type="text"
                                className="form-control"
                                value={formatDateTimeDisplay(delivery.actual_delivery_registered_at)}
                                readOnly
                                disabled
                                style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                            />
                        </div>
                    )}
                </>
            )}

            {/* Observaciones */}
            <div className="mb-3">
                <label className="form-label">Observaciones</label>
                <textarea
                    className={`form-control ${fieldHasError('observations')}`}
                    name="observations"
                    value={delivery.observations || ''}
                    onChange={handleChange}
                    placeholder="Ingrese observaciones"
                    onFocus={() => onFocusRemove('observations')}
                    rows={3}
                    disabled={is_locked}
                    readOnly={is_locked}
                />
                <div className="invalid-feedback" style={{ display: 'flex' }}>
                    {fieldErrorMessage('observations')}
                </div>
            </div>
        </>
    );
};

export default DeliveryForm;

